import React from 'react';
import { Check } from 'lucide-react';

// Pricing — single subscription plan. Lists every feature of the platform so the
// page matches the product description submitted to the payment provider.
const FEATURE_GROUPS = [
    {
        title: 'Unified inbox',
        items: [
            'WhatsApp Business, Instagram DMs, Facebook Messenger and website chat in one inbox',
            'Multiple accounts per channel, each with its own inbox tab',
            'Customer names, order history and spend beside every conversation',
            '24-hour messaging window tracking with expiry alerts',
            'Send text, images, video, documents and voice notes',
            'Mark queries resolved and hand chats between team members',
        ],
    },
    {
        title: 'AI & automation',
        items: [
            'AI intent detection and smart labels on every message',
            'AI sentiment analysis (positive, neutral, negative)',
            'Unlimited custom AI agents written in plain English',
            'AI agents trained on your own instructions and knowledge',
            'Visual drag-and-drop automation builder',
            'Triggers: intent, Shopify events, customer inactivity, chat-expiry reminders',
            'Reply buttons, AI condition branches and confirmation steps',
            'Automatic tickets, notifications and human escalation',
            'Testing mode to try flows on your own number first',
        ],
    },
    {
        title: 'Commerce & marketing',
        items: [
            'Shopify integration: live orders, abandoned checkouts and customer spend',
            'Automated order confirmation and abandoned-cart recovery',
            'Order tracking board with production and stock boards',
            'Courier tracking and delivery status sync',
            'WhatsApp template management',
            'WhatsApp campaigns with delivered, read, clicked and ordered tracking',
            'Campaign revenue attribution from Shopify orders',
            'Instagram comment moderation: reply, hide and delete',
        ],
    },
    {
        title: 'Team & insights',
        items: [
            'Support tickets with assignment and status tracking',
            'Prioritised notifications for complaints, delays and system events',
            'Team members with admin and member roles',
            'Page-level permissions for every member',
            'Analytics dashboard: sentiment, top intents, reply volume, orders, automation runs',
            'Embeddable website chat widget and full-page chat link',
            'Email support',
        ],
    },
];

const Pricing = () => (
    <section id="pricing" className="w-full py-20 md:py-24 bg-card border-t border-borderLight scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="flex flex-col items-center gap-3 text-center max-w-2xl mx-auto mb-12">
                <span className="font-mono uppercase tracking-label text-textMuted text-[11px]">Pricing</span>
                <h2 className="text-3xl md:text-5xl font-bold text-ink tracking-head leading-[1.05]">
                    One plan. <span className="font-serif-italic">Everything</span> included.
                </h2>
                <p className="text-textSecondary text-[15px] leading-relaxed mt-1">
                    A simple monthly subscription per workspace. No setup fees, no per-seat charges, cancel anytime.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                {/* plan card */}
                <div className="lg:col-span-4 lg:sticky lg:top-28 bg-ink text-paper rounded-card p-8 md:p-9 shadow-hard-soft">
                    <div className="flex items-center justify-between">
                        <span className="font-mono uppercase tracking-label text-[11px] text-paper/70">Vetobase AI</span>
                        <span className="px-2.5 py-1 rounded-full bg-paper text-ink font-mono text-[10px] uppercase tracking-label">Monthly</span>
                    </div>
                    <h3 className="mt-5 text-2xl font-bold tracking-head">Business plan</h3>
                    <div className="mt-4 flex items-end gap-2 flex-wrap">
                        <span className="text-5xl font-bold tracking-head leading-none whitespace-nowrap">Rs 27,000</span>
                        <span className="text-paper/70 text-sm pb-1.5">PKR / month</span>
                    </div>
                    <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-paper/25 px-3 py-1 text-sm font-semibold">
                        US$100 <span className="text-paper/60 font-normal">/ month in US dollars</span>
                    </div>
                    <p className="mt-3 text-paper/60 text-[13px]">Billed monthly per workspace. Taxes may apply.</p>

                    <a
                        href="#contact"
                        className="mt-7 block text-center bg-paper text-ink px-6 py-3.5 rounded-full text-sm font-bold hover:bg-bone transition-colors"
                    >
                        Get started
                    </a>
                    <a
                        href="https://app.vetobase.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 block text-center border border-paper/30 text-paper px-6 py-3 rounded-full text-sm font-semibold hover:bg-white/10 transition-colors"
                    >
                        Go to workspace
                    </a>

                    <ul className="mt-7 pt-6 border-t border-white/15 flex flex-col gap-2.5 text-[13px] text-paper/80">
                        <li className="flex gap-2.5"><Check className="w-4 h-4 flex-shrink-0 mt-0.5" />All features listed here</li>
                        <li className="flex gap-2.5"><Check className="w-4 h-4 flex-shrink-0 mt-0.5" />Unlimited team members</li>
                        <li className="flex gap-2.5"><Check className="w-4 h-4 flex-shrink-0 mt-0.5" />Cancel anytime from your account</li>
                        <li className="flex gap-2.5"><Check className="w-4 h-4 flex-shrink-0 mt-0.5" />14-day refund on your first payment</li>
                    </ul>

                    <p className="mt-6 text-[11.5px] leading-relaxed text-paper/50">
                        WhatsApp conversation fees are charged by Meta directly to your WhatsApp Business account and are not included.
                        See our <a href="/terms.html" className="underline hover:text-paper">Terms</a> and <a href="/refund.html" className="underline hover:text-paper">Refund Policy</a>.
                    </p>
                </div>

                {/* feature list */}
                <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                    {FEATURE_GROUPS.map((g) => (
                        <div key={g.title} className="bg-paper border border-borderLight rounded-card p-6 md:p-7">
                            <h4 className="font-mono uppercase tracking-label text-[11px] text-textMuted mb-4">{g.title}</h4>
                            <ul className="flex flex-col gap-3">
                                {g.items.map((it) => (
                                    <li key={it} className="flex items-start gap-2.5">
                                        <span className="mt-0.5 w-5 h-5 rounded-full bg-ink text-paper flex items-center justify-center flex-shrink-0">
                                            <Check className="w-3 h-3" />
                                        </span>
                                        <span className="text-textBody text-[14px] leading-snug">{it}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </section>
);

export default Pricing;
